import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { businessService } from '../../services/businessService';
import Header from '../../components/ui/Header';
import BusinessHeader from './components/BusinessHeader';
import BusinessDescription from './components/BusinessDescription';
import WhatWeOfferSection from './components/WhatWeOfferSection';
import LocationSection from './components/LocationSection';
import BookingSection from './components/BookingSection';
import SimilarBusinesses from './components/SimilarBusinesses';
import AuthModal from './components/AuthModal';
import Icon from '../../components/AppIcon';
import ContactWidget from '../user-profile/components/ContactWidget';
import Button from '../../components/ui/Button';

const BusinessDetail = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Enhanced business ID handling: Check both URL params and location state
  const getBusinessId = () => {
    // First, try to get from URL search params
    const urlBusinessId = searchParams?.get('id');
    if (urlBusinessId && urlBusinessId?.trim() !== '') {
      return urlBusinessId;
    }
    
    // Fallback to location state (for backward compatibility)
    const stateBusinessId = location?.state?.listingId;
    if (stateBusinessId && stateBusinessId?.trim() !== '') {
      return stateBusinessId;
    }
    
    return null;
  };
  
  const businessId = getBusinessId();

  const [business, setBusiness] = useState(null);
  const [similarBusinesses, setSimilarBusinesses] = useState([]);
  const [savedBusinesses, setSavedBusinesses] = useState([]);
  const [isPremium, setIsPremium] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Add contact widget state
  const [showContactWidget, setShowContactWidget] = useState(false);
  const [showUpgradeNotification, setShowUpgradeNotification] = useState(false);
  const [showAccessDeniedNotification, setShowAccessDeniedNotification] = useState(false);

  // Mock practitioner data (in real app, this would come from props or API)
  const practitionerData = {
    id: 'practitioner-123', // This would be the actual practitioner ID
    name: "Dr. Emma Thompson",
    specialties: ["Mindfulness Therapy", "Stress Reduction", "Natural Healing"],
    bio: "Certified holistic wellness practitioner specializing in mindfulness therapy and natural healing methods."
  };

  const handleContactPractitioner = () => {
    const authToken = localStorage?.getItem('authToken');
    if (!authToken) {
      // Redirect to login if not authenticated
      navigate('/login');
      return;
    }
    setShowContactWidget(true);
  };

  // Transform database business data to match component expectations
  const transformBusinessData = (rawBusiness) => {
    if (!rawBusiness) return null;

    // Transform business hours from array to object format
    const businessHours = rawBusiness?.business_hours?.reduce((acc, hour) => {
      const dayMap = {
        0: 'sunday',
        1: 'monday', 
        2: 'tuesday',
        3: 'wednesday',
        4: 'thursday',
        5: 'friday',
        6: 'saturday'
      };
      
      const dayName = dayMap?.[hour?.day_of_week];
      if (dayName) {
        acc[dayName] = hour?.is_closed 
          ? 'Closed'
          : `${hour?.open_time || '9:00 AM'} - ${hour?.close_time || '5:00 PM'}`;
      }
      return acc;
    }, {}) || {};

    return {
      id: rawBusiness?.id,
      name: rawBusiness?.name,
      shortDescription: rawBusiness?.short_description,
      fullDescription: rawBusiness?.description,
      headerImage: rawBusiness?.business_images?.[0]?.image_url || rawBusiness?.cover_image_url || 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&h=400&fit=crop',
      logo: rawBusiness?.logo_url || 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=100&h=100&fit=crop',
      location: [rawBusiness?.city, rawBusiness?.county]?.filter(Boolean)?.join(', ') || 'Location not specified',
      fullAddress: [rawBusiness?.address_line1, rawBusiness?.address_line2, rawBusiness?.city, rawBusiness?.county, rawBusiness?.postcode]?.filter(Boolean)?.join(', '),
      postcode: rawBusiness?.postcode,
      phone: rawBusiness?.phone,
      email: rawBusiness?.email,
      website: rawBusiness?.website_url,
      categories: rawBusiness?.categories ? [rawBusiness?.categories?.name] : [],
      rating: rawBusiness?.google_rating || rawBusiness?.average_rating || 4.8,
      reviewCount: rawBusiness?.google_review_count || rawBusiness?.total_reviews || 0,
      keyFeatures: [
        'Qualified and experienced practitioners',
        'Peaceful and calming environment', 
        'Personalised treatment plans',
        'Flexible appointment scheduling',
        'Group and individual sessions available',
        'Accessible facilities'
      ],
      certifications: [
        'Professional Certification',
        'Registered Practitioner',
        'Insured & Background Checked'
      ],
      // Mock services data - in real app would fetch from services table
      whatWeOffer: [
        {
          name: 'Primary Service',
          description: rawBusiness?.description || 'Professional holistic healing services tailored to your individual needs and wellness goals.',
          duration: '60 minutes',
          price: '65',
          icon: 'Sparkles',
          features: ['Personalized approach', 'Professional service', 'Relaxing environment', 'Expert guidance']
        }
      ],
      pricing: [
        { type: 'Individual Session', amount: '65' },
        { type: 'Package of 3 Sessions', amount: '180' },
        { type: 'Monthly Membership', amount: '120' }
      ],
      availableDates: [
        '2025-01-15',
        '2025-01-16', 
        '2025-01-17',
        '2025-01-20',
        '2025-01-21',
        '2025-01-22'
      ],
      availableTimes: ['09:00', '11:00', '14:00', '16:00', '18:00'],
      bookingUrl: rawBusiness?.website_url || '#',
      ticketUrl: '#',
      bookingNotes: 'Please arrive 10 minutes early for your appointment. Comfortable clothing recommended.',
      cancellationPolicy: '24 hours notice required for cancellations. Same-day cancellations may incur a fee.',
      openingHours: {
        monday: businessHours?.monday || '9:00 AM - 5:00 PM',
        tuesday: businessHours?.tuesday || '9:00 AM - 5:00 PM', 
        wednesday: businessHours?.wednesday || '9:00 AM - 5:00 PM',
        thursday: businessHours?.thursday || '9:00 AM - 5:00 PM',
        friday: businessHours?.friday || '9:00 AM - 5:00 PM',
        saturday: businessHours?.saturday || '10:00 AM - 4:00 PM',
        sunday: businessHours?.sunday || 'Closed'
      }
    };
  };

  useEffect(() => {
    // Handle case where no valid businessId is provided
    if (!businessId) {
      // Redirect to directory or show error
      navigate('/home-directory');
      return;
    }

    // Fetch business data from database
    const fetchBusinessData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        console.log('Fetching business data for ID:', businessId);
        
        // Fetch business details
        const { data: businessData, error: businessError } = await businessService?.getBusinessById(businessId);
        
        if (businessError) {
          console.error('Business fetch error:', businessError);
          setError(businessError);
          return;
        }

        if (!businessData) {
          console.error('No business found for ID:', businessId);
          setError('Business not found');
          return;
        }

        console.log('Raw business data:', businessData);
        
        // Transform and set business data
        const transformedBusiness = transformBusinessData(businessData);
        console.log('Transformed business data:', transformedBusiness);
        setBusiness(transformedBusiness);

        // Fetch similar businesses (simplified approach)
        try {
          const { data: similarData } = await businessService?.getBusinesses({ 
            limit: 3,
            categoryId: businessData?.categories?.id 
          });
          
          // Filter out current business and transform data
          const filteredSimilar = similarData?.filter(b => b?.id !== businessId)?.slice(0, 3) || [];
          const transformedSimilar = filteredSimilar?.map(b => ({
            id: b?.id,
            name: b?.name,
            shortDescription: b?.short_description,
            image: b?.cover_image_url || b?.logo_url || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop',
            logo: b?.logo_url,
            location: [b?.city, b?.county]?.filter(Boolean)?.join(', ') || 'Location not specified',
            categories: b?.categories ? [b?.categories?.name] : [],
            rating: b?.google_rating || b?.average_rating || 4.5,
            reviewCount: b?.google_review_count || b?.total_reviews || 50,
            priceRange: '£50-80',
            isOnline: false
          }));
          
          setSimilarBusinesses(transformedSimilar);
        } catch (similarError) {
          console.error('Error fetching similar businesses:', similarError);
          // Don't fail the whole page if similar businesses fail
        }

      } catch (fetchError) {
        console.error('Failed to fetch business:', fetchError);
        setError('Failed to load business details. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBusinessData();

    // Load saved businesses and premium status
    const saved = JSON.parse(localStorage.getItem('savedBusinesses') || '[]');
    setSavedBusinesses(saved);

    const premium = localStorage.getItem('isPremium') === 'true';
    setIsPremium(premium);

    // Handle navigation state messages
    const state = history?.state?.usr;
    if (state?.upgradeRequired) {
      setShowUpgradeNotification(true);
      setTimeout(() => setShowUpgradeNotification(false), 8000);
    }
    if (state?.accessDenied) {
      setShowAccessDeniedNotification(true);
      setTimeout(() => setShowAccessDeniedNotification(false), 8000);
    }
  }, [businessId, navigate]);

  const handleSave = (id = businessId) => {
    // Ensure we have a valid business ID before saving
    if (!id) return;
    
    const isAuthenticated = localStorage.getItem('authToken');

    if (!isAuthenticated) {
      setAuthMode('login');
      setShowAuthModal(true);
      return;
    }

    const newSaved = savedBusinesses?.includes(id)
      ? savedBusinesses?.filter(savedId => savedId !== id)
      : [...savedBusinesses, id];

    setSavedBusinesses(newSaved);
    localStorage.setItem('savedBusinesses', JSON.stringify(newSaved));
  };

  const handleShare = () => {
    // Show success message or toast
    console.log('Link copied to clipboard');
  };

  const handleAuthRequired = () => {
    setAuthMode('login');
    setShowAuthModal(true);
  };

  const handleUpgrade = () => {
    const isAuthenticated = localStorage.getItem('authToken');

    if (!isAuthenticated) {
      setAuthMode('login');
      setShowAuthModal(true);
      return;
    }

    // Simulate premium upgrade
    setIsPremium(true);
    localStorage.setItem('isPremium', 'true');

    // Hide any upgrade notifications
    setShowUpgradeNotification(false);
  };

  const handleAuthSuccess = () => {
    // Refresh saved status after authentication
    const saved = JSON.parse(localStorage.getItem('savedBusinesses') || '[]');
    setSavedBusinesses(saved);
  };

  // Early return for invalid business ID (before loading state)
  if (!businessId) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-semibold text-foreground mb-2">
                Invalid Business URL
              </h1>
              <p className="text-muted-foreground font-body mb-6">
                No business ID was provided in the URL.
              </p>
              <button
                onClick={() => navigate('/home-directory')}
                className="text-primary hover:text-primary/80 font-body font-medium transition-gentle"
              >
                Return to Directory
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="animate-pulse">
              <div className="h-96 bg-muted rounded-lg mb-6" />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                  <div className="h-64 bg-muted rounded-lg" />
                  <div className="h-48 bg-muted rounded-lg" />
                </div>
                <div className="space-y-6">
                  <div className="h-96 bg-muted rounded-lg" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-semibold text-foreground mb-2">
                {error === 'Business not found' ? 'Business Not Found' : 'Error Loading Business'}
              </h1>
              <p className="text-muted-foreground font-body mb-6">
                {error || 'The business you\'re looking for doesn\'t exist or has been removed.'}
              </p>
              <button
                onClick={() => navigate('/home-directory')}
                className="text-primary hover:text-primary/80 font-body font-medium transition-gentle"
              >
                Return to Directory
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Upgrade Required Notification */}
      {showUpgradeNotification && (
        <div className="bg-warning/10 border-b border-warning/20 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="Crown" size={20} className="text-warning" />
              <p className="text-sm font-medium text-foreground">
                Premium subscription required to add services. Upgrade to the £9.99/year plan to access service management.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleUpgrade}
                iconName="Crown"
                iconPosition="left"
                iconSize={14}
              >
                Upgrade Now
              </Button>
              <button
                onClick={() => setShowUpgradeNotification(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Access Denied Notification */}
      {showAccessDeniedNotification && (
        <div className="bg-destructive/10 border-b border-destructive/20 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={20} className="text-destructive" />
              <p className="text-sm font-medium text-foreground">
                Service forms can only be accessed from the "What We Offer" section of your business profile.
              </p>
            </div>
            <button
              onClick={() => setShowAccessDeniedNotification(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      )}

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Business Header */}
          <BusinessHeader
            business={business}
            onSave={() => handleSave()}
            onShare={handleShare}
            isSaved={savedBusinesses?.includes(businessId)}
          />

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <BusinessDescription business={business} />

              <WhatWeOfferSection
                business={business}
                isPremium={isPremium}
                onUpgrade={handleUpgrade}
                businessId={businessId}
              />

              <LocationSection business={business} />
            </div>

            {/* Right Column - Booking */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <BookingSection
                  business={business}
                  onAuthRequired={handleAuthRequired}
                />
              </div>
            </div>
          </div>

          {/* Have Questions Section - Moved above Similar Businesses */}
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-background border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Have Questions?
                  </h3>
                  <p className="text-muted-foreground">
                    Contact {practitionerData?.name} directly to ask questions about their services
                  </p>
                </div>
                <Button
                  onClick={handleContactPractitioner}
                  iconName="MessageSquare"
                  className="ml-4"
                >
                  Contact Practitioner
                </Button>
              </div>
            </div>
          </div>

          {/* Similar Businesses */}
          {similarBusinesses?.length > 0 && (
            <div className="mt-16">
              <SimilarBusinesses
                businesses={similarBusinesses}
                onSave={handleSave}
                savedBusinesses={savedBusinesses}
              />
            </div>
          )}
        </div>
      </main>

      {/* Contact Widget */}
      {showContactWidget && (
        <ContactWidget
          practitionerId={practitionerData?.id}
          practitionerName={practitionerData?.name}
          onClose={() => setShowContactWidget(false)}
        />
      )}

      {/* Authentication Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        mode={authMode}
      />
    </div>
  );
};

export default BusinessDetail;